import Vue from 'vue'
import {
  ref, computed, getCurrentInstance,
} from '@vue/composition-api'
import PayrollConfirmationApi, {
  ConfirmationResource,
  ConfirmationCellResource,
  ConfirmationFilterParams,
  ConfirmationGroup,
  IntermediateValue,
} from '@/api/v2/employee/payroll/PayrollConfirmation'
import AggregationRoleApi from '@/api/v2/employee/user-management/AggregationRole'
import { handleApiError, ApiError } from '@/utils/errorHandler'

export interface RoleOption {
  text: string
  value: string
}

interface AggregationRolesResponse {
  data: {
    data: {
      aggregationRoles?: { key: string; label: string }[]
    }
  }
}

// 列定義（key で同一性を判定し、position 順に並べる）
export interface ColumnDef {
  key: string
  label: string
  position: number
}

// 一覧の行と展開状態を同定するキー（index / ConfirmationList で共有）
export const groupKey = (
  g: { role: string; periodStart: string; periodEnd: string },
): string => `${g.role}__${g.periodStart}__${g.periodEnd}`

export function usePayrollConfirmation() {
  const vm = getCurrentInstance()!.proxy

  const roleOptions = ref<RoleOption[]>([])
  const selectedRole = ref<string | null>(null)
  const dates = ref<string[]>([])

  const confirmations = ref<ConfirmationResource[]>([])
  const groups = ref<ConfirmationGroup[]>([])

  const isLoadingRoles = ref(false)
  const isLoadingGroups = ref(false)
  const isLoading = ref(false)
  const isBuilding = ref(false)
  const isConfirming = ref(false)
  const isUnconfirming = ref(false)
  const isDeletingDrafts = ref(false)
  const updatingCellKeys = ref<Record<string, boolean>>({})

  const intermediateValues = ref<IntermediateValue[]>([])
  const isLoadingIntermediate = ref(false)
  const selectedUserName = ref<string>('')

  const sortedDates = computed<[string, string] | null>(() => {
    const valid = dates.value.filter(d => !!d)
    if (valid.length !== 2) return null
    const [a, b] = [...valid].sort()

    return [a, b]
  })

  const isValidSelection = computed(() => !!selectedRole.value && !!sortedDates.value)

  // 全件 confirmed なら確定済みとみなす（行が存在する場合のみ）
  const isAllConfirmed = computed(
    () => confirmations.value.length > 0
      && confirmations.value.every(c => c.attributes.status === 'confirmed'),
  )

  const hasConfirmations = computed(() => confirmations.value.length > 0)

  const filterParams = computed<ConfirmationFilterParams | null>(() => {
    if (!selectedRole.value || !sortedDates.value) return null

    return {
      role: selectedRole.value,
      periodStart: sortedDates.value[0],
      periodEnd: sortedDates.value[1],
    }
  })

  // 全 confirmation のセルから列定義を抽出（key で重複排除、position 順）
  const columns = computed<ColumnDef[]>(() => {
    const map = new Map<string, ColumnDef>()
    confirmations.value.forEach(conf => {
      conf.attributes.cells.data.forEach(cell => {
        const { key, label, position } = cell.attributes
        if (!map.has(key)) {
          map.set(key, { key, label, position })
        }
      })
    })

    return Array.from(map.values()).sort((a, b) => a.position - b.position)
  })

  // 行ごとに key → セル のマップを作って高速参照できるようにする
  const cellOf = (
    conf: ConfirmationResource,
    key: string,
  ): ConfirmationCellResource | undefined => conf.attributes.cells.data.find(
    cell => cell.attributes.key === key,
  )

  const fetchRoles = async () => {
    try {
      isLoadingRoles.value = true
      const res = await AggregationRoleApi.getAggregationRoles() as AggregationRolesResponse
      const roles = res.data?.data?.aggregationRoles || []
      roleOptions.value = roles.map(r => ({ text: r.label, value: r.key }))
    } catch (error) {
      handleApiError(error as ApiError, { $toast: vm.$toast })
    } finally {
      isLoadingRoles.value = false
    }
  }

  // roleKey から roleOptions の text を引く（無ければ roleKey をそのまま返す）
  const roleLabel = (roleKey: string): string => {
    const option = roleOptions.value.find(r => r.value === roleKey)

    return option ? option.text : roleKey
  }

  const fetchGroups = async () => {
    try {
      isLoadingGroups.value = true
      const res = await PayrollConfirmationApi.getConfirmationGroups()
      if (res.data.status === 'success') {
        groups.value = res.data.data
      }
    } catch (error) {
      handleApiError(error as ApiError, { $toast: vm.$toast })
    } finally {
      isLoadingGroups.value = false
    }
  }

  const fetchConfirmations = async () => {
    if (!filterParams.value) return
    try {
      isLoading.value = true
      const res = await PayrollConfirmationApi.getConfirmations(filterParams.value)
      if (res.data.status === 'success') {
        confirmations.value = res.data.data.data
      }
    } catch (error) {
      handleApiError(error as ApiError, { $toast: vm.$toast })
    } finally {
      isLoading.value = false
    }
  }

  const buildConfirmations = async (): Promise<boolean> => {
    if (!filterParams.value) return false
    try {
      isBuilding.value = true
      const res = await PayrollConfirmationApi.buildConfirmations(filterParams.value)
      if (res.data.status === 'success') {
        confirmations.value = res.data.data.data
        vm.$toast.success('確定データを作成しました')

        return true
      }

      return false
    } catch (error) {
      handleApiError(error as ApiError, { $toast: vm.$toast })

      return false
    } finally {
      isBuilding.value = false
    }
  }

  const updateCell = async (
    conf: ConfirmationResource,
    cellKey: string,
    value: number,
  ) => {
    const stateKey = `${conf.id}:${cellKey}`
    try {
      Vue.set(updatingCellKeys.value, stateKey, true)
      const res = await PayrollConfirmationApi.updateCell(
        conf.attributes.id,
        cellKey,
        value,
      )
      if (res.data.status === 'success') {
        const updated = res.data.data.data
        const index = confirmations.value.findIndex(c => c.id === updated.id)
        if (index >= 0) {
          Vue.set(confirmations.value, index, updated)
        }
      }
    } catch (error) {
      handleApiError(error as ApiError, { $toast: vm.$toast })
    } finally {
      Vue.set(updatingCellKeys.value, stateKey, false)
    }
  }

  const confirmConfirmations = async () => {
    if (!filterParams.value) return
    try {
      isConfirming.value = true
      const res = await PayrollConfirmationApi.confirmConfirmations(filterParams.value)
      if (res.data.status === 'success') {
        confirmations.value = res.data.data.data
        vm.$toast.success('最終確定しました')
      }
    } catch (error) {
      handleApiError(error as ApiError, { $toast: vm.$toast })
    } finally {
      isConfirming.value = false
    }
  }

  const unconfirmConfirmations = async () => {
    if (!filterParams.value) return
    try {
      isUnconfirming.value = true
      const res = await PayrollConfirmationApi.unconfirmConfirmations(filterParams.value)
      if (res.data.status === 'success') {
        confirmations.value = res.data.data.data
        vm.$toast.success('確定を解除しました')
      }
    } catch (error) {
      handleApiError(error as ApiError, { $toast: vm.$toast })
    } finally {
      isUnconfirming.value = false
    }
  }

  // draft グループ（role＋期間）の確定データを削除し、一覧を再取得する
  const deleteDrafts = async (group: ConfirmationGroup) => {
    try {
      isDeletingDrafts.value = true
      const res = await PayrollConfirmationApi.destroyDrafts({
        role: group.role,
        periodStart: group.periodStart,
        periodEnd: group.periodEnd,
      })
      if (res.data.status === 'success') {
        vm.$toast.success('下書きを削除しました')
        await fetchGroups()
      }
    } catch (error) {
      handleApiError(error as ApiError, { $toast: vm.$toast })
    } finally {
      isDeletingDrafts.value = false
    }
  }

  // ユーザー単位の中間値を取得する
  const fetchIntermediateValues = async (conf: ConfirmationResource) => {
    selectedUserName.value = conf.attributes.userName
    try {
      isLoadingIntermediate.value = true
      const res = await PayrollConfirmationApi.getIntermediateValues({
        role: conf.attributes.role,
        periodStart: conf.attributes.periodStart,
        periodEnd: conf.attributes.periodEnd,
        userId: conf.attributes.userId,
      })
      if (res.data.status === 'success') {
        intermediateValues.value = res.data.data
      }
    } catch (error) {
      handleApiError(error as ApiError, { $toast: vm.$toast })
    } finally {
      isLoadingIntermediate.value = false
    }
  }

  return {
    // state
    roleOptions,
    selectedRole,
    dates,
    confirmations,
    groups,
    isLoadingRoles,
    isLoadingGroups,
    isLoading,
    isBuilding,
    isConfirming,
    isUnconfirming,
    isDeletingDrafts,
    updatingCellKeys,
    intermediateValues,
    isLoadingIntermediate,
    selectedUserName,
    // computed
    sortedDates,
    isValidSelection,
    isAllConfirmed,
    hasConfirmations,
    columns,
    // helpers
    cellOf,
    roleLabel,
    // actions
    fetchRoles,
    fetchGroups,
    fetchConfirmations,
    buildConfirmations,
    updateCell,
    confirmConfirmations,
    unconfirmConfirmations,
    deleteDrafts,
    fetchIntermediateValues,
  }
}
